import { useEffect, useMemo, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { TableSkeleton, CardSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, XCircle, Search, Wallet, Clock, Ban, QrCode, Banknote, Inbox, Printer } from "lucide-react";
import { toast } from "sonner";
import { downloadReceiptPdf } from "@/lib/invoice-pdf";

export const Route = createFileRoute("/_authenticated/payments")({
  head: () => ({ meta: [{ title: "Verifikasi Pembayaran — MI Al-Munajah Admin" }] }),
  component: PaymentsPage,
});

type PaymentStatus = "pending" | "waiting_verification" | "paid" | "rejected";
type PaymentMethod = "qris" | "transfer" | "cash" | "pakasir";

type Payment = {
  id: string;
  student_id: string | null;
  nisn: string | null;
  student_name: string | null;
  class_name: string | null;
  bill_name: string;
  bill_id: string | null;
  amount: number;
  payment_method: PaymentMethod;
  status: PaymentStatus;
  payment_date: string;
  notes: string | null;
  proof_url: string | null;
  transaction_id: string | null;
  paid_at: string | null;
  rejection_reason: string | null;
  rejected_at: string | null;
  verified_at: string | null;
  created_at: string;
  pakasir_order_id: string | null;
  gateway_response: Record<string, unknown> | null;
  students?: {
    id: string; full_name: string; nisn: string | null;
    classes?: { name: string } | null;
  } | null;
};

type StatusFilter = "all" | PaymentStatus;
type MethodFilter = "all" | "qris_auto" | "transfer_auto" | "qris_manual" | "transfer_manual" | "unpaid";

function isAutoPayment(p: Pick<Payment, "pakasir_order_id">) {
  return !!(p.pakasir_order_id && p.pakasir_order_id.trim().length > 0);
}
function isUnpaid(p: Pick<Payment, "status" | "proof_url" | "pakasir_order_id">) {
  return p.status === "pending" && !p.proof_url && !isAutoPayment(p);
}
// Untuk pembayaran otomatis (Pakasir), metode aktual dipilih user di gateway
// (qris / bank_transfer / virtual_account / dst). Simpan di gateway_response.
function autoMethodKind(p: Pick<Payment, "gateway_response" | "notes" | "payment_method">): "qris" | "va" | "bank" | "ewallet" | "transfer" {
  const gr = p.gateway_response as any;
  const raw = String(
    gr?.verified?.payment_method ??
      gr?.webhook?.payment_method ??
      gr?.payment_method ??
      p.notes?.replace(/^Pakasir\s+/i, "") ??
      p.payment_method ??
      "",
  ).toLowerCase();
  if (raw.includes("qris") || raw.includes("qr")) return "qris";
  if (raw.includes("virtual") || raw.includes("va")) return "va";
  if (raw.includes("ewallet") || raw.includes("e-wallet") || raw.includes("dana") || raw.includes("ovo") || raw.includes("gopay") || raw.includes("shopee")) return "ewallet";
  if (raw.includes("bank") || raw.includes("transfer")) return "bank";
  return p.payment_method === "qris" ? "qris" : "transfer";
}

function PaymentsPage() {
  const [rows, setRows] = useCachedState<Payment[]>("payments:rows", []);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState<StatusFilter>("all");
  const [methodF, setMethodF] = useState<MethodFilter>("all");
  const [selected, setSelected] = useState<Payment | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [proofError, setProofError] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [proofSignedUrl, setProofSignedUrl] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("payments" as never)
      .select("*, students(id, full_name, nisn, classes(name))")
      .not("bill_id", "is", null)
      .order("created_at", { ascending: false });
    if (error) {
      // Table not yet provisioned — show graceful empty state instead of red error.
      const msg = error.message || "";
      if (/schema cache|does not exist|relation .* does not exist/i.test(msg)) {
        setTableMissing(true);
        setRows([]);
      } else {
        toast.error("Gagal memuat: " + msg);
      }
    } else {
      setTableMissing(false);
      setRows((data as unknown as Payment[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("payments-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  // Sinkronkan dialog detail dengan data realtime terbaru: ketika wali murid
  // mengirim ulang bukti, trigger meng-UPDATE row payment yang sama (bukan
  // INSERT baru) sehingga status & proof_url di dialog harus ikut berubah.
  useEffect(() => {
    if (!selected) return;
    const fresh = rows.find((r) => r.id === selected.id);
    if (!fresh) return;
    if (
      fresh.status !== selected.status ||
      fresh.proof_url !== selected.proof_url ||
      fresh.rejected_at !== selected.rejected_at
    ) {
      setSelected(fresh);
    }
  }, [rows, selected]);

  useEffect(() => {
    setProofSignedUrl(null);
    setProofError(false);
    if (!selected?.proof_url) return;
    const raw = selected.proof_url;
    // Bucket payment-proofs bersifat PRIVATE (workspace memblokir bucket public),
    // jadi URL `/object/public/...` yang disimpan Android tidak bisa diakses langsung.
    // Ekstrak path objek lalu buat signed URL.
    let path = raw;
    const m = raw.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/payment-proofs\/(.+?)(?:\?|$)/i);
    if (m) {
      path = decodeURIComponent(m[1]);
    } else {
      path = raw.replace(/^https?:\/\/[^/]+\//i, "").replace(/^payment-proofs\//, "");
    }
    (async () => {
      const { data, error } = await supabase.storage
        .from("payment-proofs")
        .createSignedUrl(path, 60 * 10);
      if (error) console.error("Failed to sign proof URL", error, "path:", path, "raw:", raw);
      setProofSignedUrl(data?.signedUrl ?? null);
    })();
  }, [selected?.id, selected?.proof_url]);

  // Sebuah pembayaran dianggap "pengajuan ulang bukti" jika statusnya kini
  // waiting_verification / pending TAPI sebelumnya pernah ditolak — penanda
  // historisnya: rejected_at terisi (trigger resubmit mempertahankannya).
  const resubmittedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rows) {
      if (
        (r.status === "waiting_verification" || r.status === "pending") &&
        r.rejected_at
      ) {
        ids.add(r.id);
      }
    }
    return ids;
  }, [rows]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (!r.bill_id) return false;
      if (statusF !== "all" && r.status !== statusF) return false;
      if (methodF !== "all") {
        const auto = isAutoPayment(r);
        const unpaid = isUnpaid(r);
        if (methodF === "unpaid" && !unpaid) return false;
        if (methodF === "qris_auto" && !(auto && autoMethodKind(r) === "qris")) return false;
        if (methodF === "transfer_auto" && !(auto && autoMethodKind(r) !== "qris")) return false;
        if (methodF === "qris_manual" && !(!auto && !unpaid && r.payment_method === "qris")) return false;
        if (methodF === "transfer_manual" && !(!auto && !unpaid && r.payment_method === "transfer")) return false;
      }
      if (!ql) return true;
      const name = r.students?.full_name ?? r.student_name ?? "";
      const nisn = r.students?.nisn ?? r.nisn ?? "";
      return (
        name.toLowerCase().includes(ql) ||
        nisn.toLowerCase().includes(ql) ||
        r.bill_name.toLowerCase().includes(ql) ||
        (r.transaction_id ?? "").toLowerCase().includes(ql)
      );
    });
  }, [rows, q, statusF, methodF]);

  const stats = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const totalToday = rows
      .filter((r) => Boolean(r.bill_id))
      .filter((r) => r.status === "paid" && new Date(r.paid_at ?? r.payment_date) >= today)
      .reduce((a, r) => a + Number(r.amount), 0);
    const billLinkedRows = rows.filter((r) => Boolean(r.bill_id));
    return {
      totalToday,
      pending: billLinkedRows.filter((r) => r.status === "pending" || r.status === "waiting_verification").length,
      paid: billLinkedRows.filter((r) => r.status === "paid").length,
      rejected: billLinkedRows.filter((r) => r.status === "rejected").length,
    };
  }, [rows]);

  const approve = async (p: Payment) => {
    setBusy(true);
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("payments" as never)
      .update({
        status: "paid",
        paid_at: new Date().toISOString(),
        verified_at: new Date().toISOString(),
        verified_by: u.user?.id,
        rejection_reason: null,
        rejected_at: null,
      } as never)
      .eq("id", p.id);
    if (!error && p.bill_id) {
      await supabase
        .from("student_bills")
        .update({ status: "paid", payment_id: p.id })
        .eq("id", p.bill_id);
    }
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Pembayaran disetujui");
    setSelected(null);
  };

  const reject = async () => {
    if (!selected) return;
    if (!rejectReason.trim()) return toast.error("Alasan penolakan wajib diisi");
    setBusy(true);
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("payments" as never)
      .update({
        status: "rejected",
        rejection_reason: rejectReason.trim(),
        rejected_at: new Date().toISOString(),
        verified_at: new Date().toISOString(),
        verified_by: u.user?.id,
        paid_at: null,
      } as never)
      .eq("id", selected.id);
    // Catatan: status student_bills tidak diubah agar riwayat penolakan tetap utuh
    // dan payment yang ditolak tetap terkait dengan tagihan yang sama untuk audit.
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Pembayaran ditolak");
    setRejectOpen(false);
    setRejectReason("");
    setSelected(null);
  };

  const studentName = (r: Payment) => r.students?.full_name ?? r.student_name ?? "—";
  const studentNisn = (r: Payment) => r.students?.nisn ?? r.nisn ?? "—";
  const studentClass = (r: Payment) => r.students?.classes?.name ?? r.class_name ?? "—";

  const printReceipt = async (p: Payment) => {
    try {
      let invoiceNumber: string | null = null;
      if (p.bill_id) {
        const { data } = await supabase
          .from("student_bills")
          .select("invoice_number")
          .eq("id", p.bill_id)
          .maybeSingle();
        invoiceNumber = data?.invoice_number ?? null;
      }
      await downloadReceiptPdf({
        invoice_number: invoiceNumber,
        bill_name: p.bill_name,
        amount: Number(p.amount),
        payment_method: (p.payment_method === "qris" ? "qris" : p.payment_method === "cash" ? "cash" : "transfer"),
        pakasir_order_id: p.pakasir_order_id,
        payment_date: p.payment_date,
        paid_at: p.paid_at,
        proof_url: p.proof_url,
        students: p.students,
        student_name: p.student_name,
        nisn: p.nisn,
        class_name: p.class_name,
      });
    } catch (e) {
      toast.error("Gagal membuat kwitansi: " + ((e as Error)?.message ?? String(e)));
    }
  };

  return (
    <div>
      <PageHeader title="Verifikasi Pembayaran" description="Tinjau, setujui, atau tolak pembayaran siswa secara realtime." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Total Lunas Hari Ini" value={formatIDR(stats.totalToday)} icon={Wallet} tint="bg-primary/10 text-primary" />
        <StatCard label="Menunggu Verifikasi" value={stats.pending.toString()} icon={Clock} tint="bg-warning/10 text-warning" />
        <StatCard label="Pembayaran Lunas" value={stats.paid.toString()} icon={CheckCircle2} tint="bg-success/10 text-success" />
        <StatCard label="Pembayaran Ditolak" value={stats.rejected.toString()} icon={Ban} tint="bg-destructive/10 text-destructive" />
      </div>

      <Card className="p-4 rounded-2xl">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, NISN, tagihan, ID transaksi…" className="pl-9" />
          </div>
          <Select value={statusF} onValueChange={(v) => setStatusF(v as StatusFilter)}>
            <SelectTrigger className="md:w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="waiting_verification">Menunggu Verifikasi</SelectItem>
              <SelectItem value="paid">Lunas</SelectItem>
              <SelectItem value="rejected">Ditolak</SelectItem>
            </SelectContent>
          </Select>
          <Select value={methodF} onValueChange={(v) => setMethodF(v as MethodFilter)}>
            <SelectTrigger className="md:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Metode</SelectItem>
              <SelectItem value="unpaid">Belum Dibayar</SelectItem>
              <SelectItem value="qris_auto">QRIS Otomatis</SelectItem>
              <SelectItem value="transfer_auto">Transfer Bank Otomatis</SelectItem>
              <SelectItem value="qris_manual">QRIS Manual</SelectItem>
              <SelectItem value="transfer_manual">Transfer Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop / tablet table */}
        <div className="hidden sm:block rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Siswa</TableHead>
                <TableHead>NISN</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead>Tagihan</TableHead>
                <TableHead className="text-right">Nominal</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton columns={9} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-12">
                    <div className="flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <Inbox className="w-6 h-6" />
                      </div>
                      <div className="font-medium text-foreground">Belum ada pembayaran.</div>
                      {tableMissing && (
                        <div className="text-xs">Modul pembayaran sedang disiapkan. Halaman akan terisi otomatis saat data tersedia.</div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : filtered.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => setSelected(p)}>
                  <TableCell className="font-medium">{studentName(p)}</TableCell>
                  <TableCell className="text-muted-foreground">{studentNisn(p)}</TableCell>
                  <TableCell>{studentClass(p)}</TableCell>
                  <TableCell>{p.bill_name}</TableCell>
                  <TableCell className="text-right font-semibold">{formatIDR(p.amount)}</TableCell>
                  <TableCell><MethodBadge p={p} /></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(p.payment_date)}</TableCell>
                  <TableCell><StatusBadge s={p.status} resubmitted={resubmittedIds.has(p.id)} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                      {p.status === "paid" && (
                        <Button size="icon" variant="ghost" title="Cetak Kwitansi" onClick={() => printReceipt(p)}>
                          <Printer className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setSelected(p)}>Detail</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile card list */}
        <div className="sm:hidden space-y-2.5">
          {loading ? (
            <CardSkeleton lines={4} />
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center gap-2 text-muted-foreground py-10">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Inbox className="w-6 h-6" />
              </div>
              <div className="font-medium text-foreground">Belum ada pembayaran.</div>
              {tableMissing && (
                <div className="text-xs px-6">Modul pembayaran sedang disiapkan. Halaman akan terisi otomatis saat data tersedia.</div>
              )}
            </div>
          ) : filtered.map((p) => {
            const canConfirm = p.status === "pending" || p.status === "waiting_verification";
            return (
              <div
                key={p.id}
                className="rounded-2xl border bg-card p-3.5 shadow-sm active:scale-[0.99] transition-transform"
                onClick={() => setSelected(p)}
              >
                {/* Header: name + status */}
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[15px] leading-tight truncate">{studentName(p)}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      NISN {studentNisn(p) || "—"} · {studentClass(p) || "—"}
                    </div>
                  </div>
                  <StatusBadge s={p.status} resubmitted={resubmittedIds.has(p.id)} />
                </div>

                {/* Bill + amount row */}
                <div className="flex items-end justify-between gap-2 mt-2.5">
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Tagihan</div>
                    <div className="text-sm font-medium truncate">{p.bill_name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Nominal</div>
                    <div className="text-base font-bold text-primary">{formatIDR(p.amount)}</div>
                  </div>
                </div>

                {/* Method + date */}
                <div className="flex items-center justify-between gap-2 mt-3 pt-2.5 border-t border-dashed">
                  <MethodBadge p={p} />
                  <div className="text-[11px] text-muted-foreground">{formatDate(p.payment_date)}</div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  {p.status === "paid" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => printReceipt(p)}
                      className="flex-1 h-9 gap-1.5"
                    >
                      <Printer className="w-4 h-4" /> Cetak
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={canConfirm || p.status === "paid" ? "outline" : "default"}
                    onClick={() => setSelected(p)}
                    className="flex-1 h-9"
                  >
                    Detail
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Pembayaran</DialogTitle>
            <DialogDescription>Periksa data sebelum menyetujui atau menolak.</DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <Info label="Nama Siswa" value={studentName(selected)} />
                <Info label="NISN" value={studentNisn(selected)} />
                <Info label="Kelas" value={studentClass(selected)} />
                <Info label="Status" value={<StatusBadge s={selected.status} resubmitted={resubmittedIds.has(selected.id)} />} />
                <Info label="Nama Tagihan" value={selected.bill_name} />
                <Info label="Nominal" value={<span className="font-semibold">{formatIDR(selected.amount)}</span>} />
                <Info label="Metode" value={<MethodBadge p={selected} />} />
                <Info label="Tanggal Pembayaran" value={formatDate(selected.payment_date)} />
                {selected.payment_method === "qris" && (
                  <>
                    <Info label="Transaction ID" value={selected.transaction_id ?? "—"} />
                    <Info label="Diterima" value={selected.paid_at ? formatDate(selected.paid_at) : "—"} />
                  </>
                )}
              </div>

              {selected.notes && !isAutoPayment(selected) && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Catatan Siswa</div>
                  <div className="rounded-lg border bg-muted/30 p-3 text-sm">{selected.notes}</div>
                </div>
              )}

              {(selected.payment_method === "transfer" || selected.payment_method === "qris") && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-muted-foreground">
                      {selected.payment_method === "qris" ? "Bukti Pembayaran QRIS" : "Bukti Transfer"}
                    </div>
                    {selected.proof_url && !proofError && (proofSignedUrl ?? selected.proof_url) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setLightboxOpen(true)}
                      >
                        Perbesar
                      </Button>
                    )}
                  </div>
                  {selected.proof_url ? (
                    proofError ? (
                      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">
                        Gagal memuat bukti pembayaran. Silakan minta siswa mengunggah ulang.
                      </div>
                    ) : !proofSignedUrl ? (
                      <div className="rounded-lg border bg-muted/30 h-64 flex items-center justify-center text-sm text-muted-foreground">
                        Memuat bukti…
                      </div>
                    ) : (
                      <div className="w-full rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                        <img
                          src={proofSignedUrl}
                          alt="Bukti Pembayaran"
                          loading="lazy"
                          onClick={() => setLightboxOpen(true)}
                          className="w-full max-h-[600px] object-contain cursor-zoom-in"
                          onError={() => setProofError(true)}
                        />
                      </div>
                    )
                  ) : (
                    <div className="rounded-lg border h-24 flex items-center justify-center text-sm text-muted-foreground">
                      Tidak ada bukti pembayaran
                    </div>
                  )}
                </div>
              )}

              {selected.status === "rejected" && selected.rejection_reason && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                  <div className="text-xs font-medium text-destructive mb-1">Alasan Penolakan</div>
                  {selected.rejection_reason}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {(selected?.status === "pending" || selected?.status === "waiting_verification") && (
              <>
                <Button variant="destructive" disabled={busy} onClick={() => setRejectOpen(true)}>
                  <XCircle className="w-4 h-4" /> Tolak Pembayaran
                </Button>
                <Button disabled={busy} onClick={() => selected && approve(selected)}>
                  <CheckCircle2 className="w-4 h-4" /> Setujui Pembayaran
                </Button>
              </>
            )}
            {selected && selected.status !== "pending" && selected.status !== "waiting_verification" && (
              <>
                {selected.status === "paid" && (
                  <Button variant="outline" onClick={() => printReceipt(selected)}>
                    <Printer className="w-4 h-4" /> Cetak Kwitansi
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelected(null)}>Tutup</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tolak Pembayaran</DialogTitle>
            <DialogDescription>Pilih alasan cepat atau tulis sendiri agar siswa dapat memperbaiki.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {[
              "Bukti transfer buram",
              "Nominal tidak sesuai",
              "Bukti transfer tidak valid",
              "Transfer belum masuk rekening",
            ].map((r) => (
              <Button
                key={r}
                type="button"
                size="sm"
                variant={rejectReason === r ? "default" : "outline"}
                onClick={() => setRejectReason(r)}
              >
                {r}
              </Button>
            ))}
          </div>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Tulis alasan lainnya…"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Batal</Button>
            <Button variant="destructive" disabled={busy || !rejectReason.trim()} onClick={reject}>Kirim Penolakan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[90vw] p-2 bg-background/95">
          <DialogHeader className="px-2 pt-1">
            <DialogTitle className="text-sm">Bukti Transfer</DialogTitle>
            <DialogDescription className="sr-only">Pratinjau bukti transfer ukuran penuh.</DialogDescription>
          </DialogHeader>
          <div
            className="w-full max-h-[85vh] overflow-auto flex items-center justify-center bg-black/80 rounded-md cursor-zoom-out"
            onClick={() => setLightboxOpen(false)}
          >
            {proofSignedUrl ? (
              <img
                src={proofSignedUrl}
                alt="Bukti Transfer"
                className="max-w-full max-h-[85vh] object-contain"
              />
            ) : (
              <div className="p-10 text-sm text-muted-foreground">Tidak tersedia</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tint }: {
  label: string; value: string; icon: React.ComponentType<{ className?: string }>; tint: string;
}) {
  return (
    <Card className="p-4 rounded-2xl border-border/60">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tint} mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-xl sm:text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </Card>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 break-words">{value}</div>
    </div>
  );
}


function StatusBadge({ s, resubmitted }: { s: PaymentStatus; resubmitted?: boolean }) {
  const needsCheck = s === "pending" || s === "waiting_verification";
  if (needsCheck) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-warning/40 bg-warning/15 text-warning btn-confirm-modern">
        <span className="dot-ping" />
        {resubmitted ? "Bukti Diperbarui · Cek" : "Perlu Dicek"}
      </span>
    );
  }
  const map: Record<PaymentStatus, { label: string; cls: string }> = {
    pending: { label: "Pending", cls: "bg-muted text-muted-foreground border-border" },
    waiting_verification: { label: "Menunggu", cls: "bg-warning/15 text-warning border-warning/30" },
    paid: { label: "Lunas", cls: "bg-success/15 text-success border-success/30" },
    rejected: { label: "Ditolak", cls: "bg-destructive/15 text-destructive border-destructive/30" },
  };
  const v = map[s] ?? map.pending;
  return <Badge variant="outline" className={v.cls}>{v.label}</Badge>;
}

function MethodBadge({ p }: { p: Pick<Payment, "payment_method" | "status" | "proof_url" | "pakasir_order_id" | "gateway_response" | "notes"> }) {
  if (isUnpaid(p)) {
    return <Badge variant="outline" className="gap-1 border-muted-foreground/30 text-muted-foreground"><Clock className="w-3 h-3" /> Belum Dibayar</Badge>;
  }
  const auto = isAutoPayment(p);
  if (auto) {
    const kind = autoMethodKind(p);
    const label =
      kind === "qris" ? "QRIS Otomatis" :
      kind === "va" ? "Virtual Account Otomatis" :
      kind === "ewallet" ? "E-Wallet Otomatis" :
      kind === "bank" ? "Transfer Bank Otomatis" :
      "Transfer Otomatis";
    const Icon = kind === "qris" ? QrCode : Banknote;
    return (
      <Badge variant="outline" className="gap-1">
        <Icon className="w-3 h-3" /> {label}
      </Badge>
    );
  }
  if (p.payment_method === "cash") {
    return (
      <Badge variant="outline" className="gap-1">
        <Wallet className="w-3 h-3" /> Pembayaran Tunai
      </Badge>
    );
  }
  if (p.payment_method === "qris") {
    return (
      <Badge variant="outline" className="gap-1">
        <QrCode className="w-3 h-3" /> QRIS Manual
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1">
      <Banknote className="w-3 h-3" /> Transfer Manual
    </Badge>
  );
}

function formatIDR(n: number) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);
}

function formatDate(d: string) {
  return new Date(d).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
